import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, Loader2 } from "lucide-react";
import { adminList, adminCreate, adminUpdate, adminDelete, adminUpload, resolveImg } from "@/lib/api";
import { CONTENT_CONFIG } from "@/admin/contentConfig";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyFromFields = (fields) => {
  const o = {};
  fields.forEach((f) => {
    if (f.type === "list" || f.type === "images" || f.type === "faqlist") o[f.key] = [];
    else if (f.type === "checkbox") o[f.key] = false;
    else if (f.type === "number") o[f.key] = 0;
    else o[f.key] = "";
  });
  return o;
};

const ImagesField = ({ value = [], onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const res = await adminUpload(file);
        onChange([...(value || []), res.url]);
      }
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {(value || []).map((url, i) => (
          <div key={url} className="relative h-20 w-20 rounded-md overflow-hidden border border-border">
            <img src={resolveImg(url)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="h-20 w-20 rounded-md border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary" data-testid="upload-image-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*,video/*" multiple className="hidden" onChange={upload} />
    </div>
  );
};

const SingleImageField = ({ value = "", onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await adminUpload(file);
      onChange(res.url);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" />
      {value && <img src={resolveImg(value)} alt="" className="h-24 rounded-md object-cover border border-border" />}
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 text-sm rounded-md border border-border px-3 py-1.5 hover:border-primary">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
    </div>
  );
};

const FaqListField = ({ value = [], onChange }) => {
  const update = (i, k, v) => onChange(value.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  return (
    <div className="space-y-3">
      {(value || []).map((f, i) => (
        <div key={i} className="border border-border rounded-md p-3 space-y-2 bg-muted/30">
          <Input value={f.question} onChange={(e) => update(i, "question", e.target.value)} placeholder="Question" />
          <Textarea value={f.answer} onChange={(e) => update(i, "answer", e.target.value)} placeholder="Answer" rows={2} />
          <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="text-xs text-destructive">Remove</button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...(value || []), { question: "", answer: "" }])} className="text-sm rounded-md border border-border px-3 py-1.5 hover:border-primary">+ Add FAQ</button>
    </div>
  );
};

const Field = ({ f, value, onChange }) => {
  if (f.type === "textarea") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={f.big ? 8 : 3} placeholder={f.label} />;
  if (f.type === "number") return <Input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} />;
  if (f.type === "checkbox") return <div className="flex items-center gap-2"><Checkbox checked={!!value} onCheckedChange={onChange} /> <span className="text-sm text-muted-foreground">Yes</span></div>;
  if (f.type === "select") return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent>
    </Select>
  );
  if (f.type === "list") return <Textarea value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").filter((x) => x.trim() !== ""))} rows={4} placeholder="One item per line" />;
  if (f.type === "images") return <ImagesField value={value} onChange={onChange} />;
  if (f.type === "image-single") return <SingleImageField value={value} onChange={onChange} />;
  if (f.type === "faqlist") return <FaqListField value={value} onChange={onChange} />;
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.label} />;
};

const ContentManager = () => {
  const { collection } = useParams();
  const config = CONTENT_CONFIG[collection];
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const { data: items = [], isLoading } = useQuery({ queryKey: ["admin", collection], queryFn: () => adminList(collection), enabled: !!config });

  if (!config) return <p>Unknown section</p>;

  const openNew = () => { setEditing(null); setForm(emptyFromFields(config.fields)); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...emptyFromFields(config.fields), ...item }); setOpen(true); };

  const save = async () => {
    for (const f of config.fields) if (f.required && !form[f.key]) { toast.error(`${f.label} is required`); return; }
    setSaving(true);
    try {
      if (editing) await adminUpdate(collection, editing.id, form);
      else await adminCreate(collection, form);
      qc.invalidateQueries({ queryKey: ["admin", collection] });
      toast.success("Saved");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await adminDelete(collection, id);
      qc.invalidateQueries({ queryKey: ["admin", collection] });
      toast.success("Deleted");
    } catch { toast.error("Could not delete. Please retry."); }
  };

  return (
    <div data-testid={`admin-${collection}`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl text-secondary">{config.title}</h1>
          <p className="text-muted-foreground mt-1">{items.length} item(s)</p>
        </div>
        <button onClick={openNew} data-testid="add-new-button" className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-semibold text-white hover:bg-[#CC5204]"><Plus className="h-4 w-4" /> New {config.singular}</button>
      </div>

      {isLoading ? <p className="mt-8 text-muted-foreground">Loading…</p> : (
        <div className="mt-8 grid gap-3">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-4 bg-white border border-border rounded-xl px-5 py-4" data-testid={`item-${item.id}`}>
              <div className="min-w-0">
                <h3 className="font-medium text-secondary truncate">{item[config.titleField]}</h3>
                <p className="text-xs text-muted-foreground capitalize">{item[config.subtitleField]}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => openEdit(item)} aria-label={`Edit ${item[config.titleField] || "item"}`} data-testid={`edit-${item.id}`} className="p-2 rounded-md border border-border text-secondary hover:border-primary"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(item.id)} aria-label={`Delete ${item[config.titleField] || "item"}`} data-testid={`delete-${item.id}`} className="p-2 rounded-md border border-border text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-3xl text-secondary">{editing ? "Edit" : "New"} {config.singular}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {config.fields.map((f) => (
              <div key={f.key}>
                <Label className="text-sm">{f.label}{f.required && " *"}</Label>
                {f.hint && <p className="text-xs text-muted-foreground mb-1">{f.hint}</p>}
                <div className="mt-1"><Field f={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} /></div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setOpen(false)} className="rounded-md border border-border px-5 py-2.5 font-medium text-secondary">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-button" className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-2.5 font-semibold text-white hover:bg-[#CC5204] disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
